import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import type { StickyNotesProps, StickyNotesRef } from './StickyNotes.data';
import styles from './StickyNotes.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './StickyNotes.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import StickyNote from 'components/molecules/sticky-note/StickyNote';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';

export default function StickyNotes({
  isVisible,
  className,
  blok,
  ref,
}: StickyNotesProps & { ref?: Ref<StickyNotesRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);

  useThemeScrollBlock(elementRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground?.color || '',
    background: blok.accent_color_background?.color || '',
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.stickyNotes, className)}
      {...sbEditable(blok)}
    >
      {blok.notes.map((note, index) => (
        <StickyNote key={note._uid} note={note} className={styles.note} index={index} />
      ))}
    </div>
  );
}
